import React, { useCallback, useContext, useEffect, useId, useRef, useState } from 'react';
import styled from 'styled-components';
import { useFloating, autoUpdate, offset, flip, size, FloatingPortal } from '@floating-ui/react';
import { ModalLayerContext } from './Modal';

// Lista wyboru w wyglądzie list rozwijanych panelu (2026-09-29).
//
// Systemowy `<select>` otwiera listę rysowaną przez przeglądarkę i system (szare zaznaczenie,
// kanciaste rogi, czcionka systemu), więc wyglądał obco obok list w formularzach sprzętu,
// rezerwacji i kalendarza. Tamte to przycisk z obramowaniem pola i własną listą
// (`FilterDropdown` — kopiowany per strona, patrz P2.3 w CLAUDE.md). Ten komponent odtwarza
// ten sam wygląd jako JEDNA, dostępna implementacja do nowych ekranów: rola `listbox`,
// strzałki, Home/End, Enter/Spacja, Escape, zamknięcie klikiem obok. Położenie listy liczy
// @floating-ui (odwraca się do góry i skraca, gdy brakuje miejsca), a lista jest w portalu,
// więc nie przycina jej żaden przodek z `overflow: hidden`.

const Trigger = styled.button`
  width: 100%;
  padding: 12px 12px;
  border: 2px solid var(--input-border);
  border-radius: 12px;
  font: inherit;
  font-size: 0.95rem;
  font-weight: 500;
  line-height: 1.2;
  background: var(--input-bg);
  color: var(--text);
  text-align: left;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  transition: border-color 0.2s, box-shadow 0.2s, background 0.2s;

  span { min-width: 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  svg { flex-shrink: 0; color: var(--text-muted); transition: transform 0.2s, color 0.2s; }

  &:hover:not(:disabled) { border-color: var(--primary); }
  &:focus-visible { outline: none; border-color: var(--primary); background: var(--card-bg); box-shadow: var(--focus-ring); }
  &:disabled { opacity: 0.55; cursor: not-allowed; }
  &[aria-expanded='true'] {
    border-color: var(--primary);
    background: var(--card-bg);
    box-shadow: var(--focus-ring);
    svg { color: var(--primary); transform: rotate(180deg); }
  }
  @media (prefers-reduced-motion: reduce) { transition: none; svg { transition: none; } }
`;

const List = styled.ul`
  margin: 0;
  padding: 6px;
  list-style: none;
  background: var(--card-bg);
  border: 1px solid var(--input-border);
  border-radius: 12px;
  box-shadow: 0 8px 32px var(--shadow-color);
  overflow-y: auto;
  box-sizing: border-box;
  /* Lista otwarta z okna na warstwie potwierdzen (--z-confirm) musi stac NAD nim; zwykla
     warstwa list (--z-popover) lezy pod takim oknem i lista otwieralaby sie niewidoczna. */
  z-index: ${(p) => (p.$elevated ? 'calc(var(--z-confirm) + 1)' : 'var(--z-popover)')};
  outline: none;
`;

const Option = styled.li`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 500;
  color: var(--text);
  cursor: pointer;

  .check { width: 16px; flex-shrink: 0; color: var(--primary); display: inline-flex; }
  &[data-active='true'] { background: var(--table-row-hover, var(--hover-bg)); }
  &[aria-selected='true'] { background: rgba(59, 130, 246, 0.15); color: var(--primary); font-weight: 600; }
`;

const Chevron = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// Położenie listy śledzone BEZ obserwowania jej własnego rozmiaru.
//
// Domyślne `autoUpdate` obserwuje przez ResizeObserver przycisk ORAZ listę. Rozmiar listy
// ustawiamy sami w `size.apply` (szerokość przycisku, wysokość z miejsca na ekranie), więc gdy
// pomiar zmienia się z klatki na klatkę, lista zmienia rozmiar w trakcie obsługi własnego
// obserwatora, a przeglądarka zgłasza „ResizeObserver loop completed with undelivered
// notifications”. Tak jest, gdy listę otworzy się w oknie modalnym, które jeszcze wjeżdża ze
// skalą 0,98 → 1: szerokość przycisku na ekranie rośnie wtedy co klatkę. Zabezpieczenie
// wbudowane w floating-ui (issue 1740) obejmuje tylko zmianę rozmiaru samego przycisku.
// Dlatego obserwowany jest wyłącznie przycisk; jego ruch, przewijanie i zmianę rozmiaru okna
// przeglądarki dalej śledzi `autoUpdate`, a zmianę pozycji listy komponent zgłasza sam.
function sledzBezRozmiaruListy(reference, floating, update) {
  const zatrzymaj = autoUpdate(reference, floating, update, { elementResize: false });
  if (typeof ResizeObserver !== 'function' || !(reference instanceof Element)) return zatrzymaj;
  const obserwator = new ResizeObserver(() => update());
  obserwator.observe(reference);
  return () => { obserwator.disconnect(); zatrzymaj(); };
}

const Check = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <polyline points="20,6 9,17 4,12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/**
 * @param {{ id?: string, value: string, onChange: (v: string) => void,
 *           options: {value: string, label: string}[], dataCy?: string, 'aria-label'?: string }} props
 * `id` trafia na przycisk, więc `<FieldLabel htmlFor={id}>` działa jak przy zwykłym polu.
 */
export default function SelectDropdown({ id, value, onChange, options, dataCy, ...rest }) {
  const autoId = useId();
  const listId = `${id || autoId}-list`;
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const listRef = useRef(null);
  const layer = useContext(ModalLayerContext);
  const selectedIndex = Math.max(0, options.findIndex((o) => o.value === value));
  const selected = options[selectedIndex];

  const { refs, floatingStyles, update } = useFloating({
    open,
    placement: 'bottom-start',
    whileElementsMounted: sledzBezRozmiaruListy,
    middleware: [
      offset(6),
      flip({ padding: 12 }),
      size({
        padding: 12,
        apply({ availableHeight, rects, elements }) {
          Object.assign(elements.floating.style, {
            minWidth: `${rects.reference.width}px`,
            maxHeight: `${Math.max(140, Math.min(320, availableHeight))}px`,
          });
        },
      }),
    ],
  });

  const openList = () => { setActive(selectedIndex); setOpen(true); };
  const close = (focusTrigger = true) => {
    setOpen(false);
    if (focusTrigger && refs.domReference.current) refs.domReference.current.focus();
  };
  const choose = (i) => {
    const opt = options[i];
    if (opt && opt.value !== value) onChange(opt.value);
    close();
  };

  // Fokus na listę w chwili, gdy POJAWI SIĘ w dokumencie — klawiatura od razu steruje
  // pozycjami. Nie w efekcie na `open`: FloatingPortal montuje listę o jedno odświeżenie
  // później, więc efekt nie zastawał jeszcze elementu i fokus zostawał na przycisku.
  const { setFloating } = refs;
  const attachList = useCallback((el) => {
    setFloating(el);
    listRef.current = el;
    if (el) el.focus({ preventScroll: true });
  }, [setFloating]);

  // Rozmiar listy nie jest obserwowany (patrz sledzBezRozmiaruListy), więc gdy pozycje zmienią
  // się przy otwartej liście, położenie przeliczamy sami: lista odwrócona nad przycisk stoi na
  // wysokości zależnej od własnej.
  useEffect(() => { if (open) update(); }, [open, options, update]);

  // Aktywna pozycja zawsze widoczna w przewijanej liście.
  useEffect(() => {
    if (!open || !listRef.current) return;
    const el = listRef.current.children[active];
    if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
  }, [open, active]);

  // Klik poza przyciskiem i listą zamyka bez zmiany wartości.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      const t = e.target;
      if (refs.domReference.current?.contains(t) || refs.floating.current?.contains(t)) return;
      close(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]); // celowo tylko `open`: refs są stabilne, a close nie zależy od stanu

  const onTriggerKey = (e) => {
    if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(e.key)) {
      e.preventDefault();
      openList();
    }
  };

  const onListKey = (e) => {
    const last = options.length - 1;
    const moves = {
      ArrowDown: () => setActive((a) => Math.min(last, a + 1)),
      ArrowUp: () => setActive((a) => Math.max(0, a - 1)),
      Home: () => setActive(0),
      End: () => setActive(last),
      Enter: () => choose(active),
      ' ': () => choose(active),
      Escape: () => close(),
    };
    // Tab zamyka liste i ODDAJE fokus przyciskowi, zanim przegladarka wykona swoj ruch: dalej
    // idzie wiec od przycisku, a nie od listy doklejonej na koncu dokumentu (z okna modalnego
    // fokus uciekal wtedy na strone pod spodem).
    if (e.key === 'Tab') { close(); return; }
    // Esc zamyka TYLKO liste. Lista stoi w portalu, wiec bez zatrzymania zdarzenie dochodzilo do
    // nasluchu Esc okna modalnego, z ktorego ja otwarto, i zamykalo takze to okno.
    if (e.key === 'Escape') e.stopPropagation();
    if (moves[e.key]) { e.preventDefault(); moves[e.key](); }
  };

  return (
    <>
      <Trigger
        ref={refs.setReference}
        id={id}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        onClick={() => (open ? close() : openList())}
        onKeyDown={onTriggerKey}
        data-cy={dataCy}
        data-value={value}
        {...rest}
      >
        <span>{selected ? selected.label : ''}</span>
        <Chevron />
      </Trigger>
      {open && (
        <FloatingPortal>
          <List
            ref={attachList}
            id={listId}
            role="listbox"
            tabIndex={-1}
            aria-activedescendant={`${listId}-${active}`}
            aria-labelledby={id}
            onKeyDown={onListKey}
            style={floatingStyles}
            $elevated={Boolean(layer && layer.elevated)}
            data-cy={dataCy ? `${dataCy}-list` : undefined}
          >
            {options.map((o, i) => (
              <Option
                key={o.value}
                id={`${listId}-${i}`}
                role="option"
                aria-selected={o.value === value}
                data-active={i === active}
                onMouseEnter={() => setActive(i)}
                onClick={() => choose(i)}
                data-cy={dataCy ? `${dataCy}-option` : undefined}
                data-value={o.value}
              >
                <span className="check">{o.value === value && <Check />}</span>
                {o.label}
              </Option>
            ))}
          </List>
        </FloatingPortal>
      )}
    </>
  );
}
